import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useCallback, useState } from "react";
import { AppHeaderBar } from "@/components/ui/app-header";
import type { ReactElement } from "react";
import Link from "next/link";
import { BarChart3, Bell, Github, SettingsIcon } from "lucide-react";
import Sidebar from "./sidebar/Sidebar";
import { OrgUrlSyncProvider } from "./OrgUrlSyncProvider";
import AppSwitcherRail from "./sidebar/AppSwitcherRail";
import { useActiveApp, isCodesmithPath, isSettingsPath, isStandaloneAccountPath } from "./sidebar/useActiveApp";
import SettingsSidebar from "./sidebar/SettingsSidebar";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import SidebarSkeleton from "./sidebar/SidebarSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { SessionSkeleton } from "./codesmith/SessionSkeleton";
import { BlacksmithLogomarkAnimationSVG, BlacksmithLogomarkSVG } from "./BlacksmithLogoSVG";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { RAIL_REDESIGN_CLASS, RAIL_ROW, RAIL_ROW_ACTIVE } from "./sidebar/SidebarGlobalChrome";
import { readCachedSidebarVariant } from "./codesmith/sidebarVariant";
import { RailStatusWidgetSkeleton } from "./sidebar/StatusIndicator";
import { useLocalStorage } from "usehooks-ts";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { SidebarMainContent } from "./SidebarMainContent";
import { cn } from "@/lib/utils";
import { ShortcutsModal } from "./ShortcutsModal";
import LiveMetricsSheetLoader from "./sidebar/LiveMetricsSheetLoader";
import { useShortcuts } from "@/hooks/useShortcuts";
import { CommandPalette } from "./CommandPalette";
import { useWorkflowCache } from "@/hooks/useWorkflowCache";
import { usePrefetchAssistantSessions } from "@/hooks/usePrefetchAssistantSessions";
import { usePrefetchCodesmithChunk } from "@/hooks/usePrefetchCodesmithChunk";
import { RAIL_REDESIGN_WIDTH, RAIL_WIDTH } from "./sidebar/railConstants";
import { getCachedCodesmithEnabled, getCachedLastActiveOrg } from "./sidebar/useCodesmithEnabledCache";

// Helper function to read sidebar state from localStorage synchronously
// This ensures the skeleton matches the actual sidebar state even during initial render
function getSidebarStateFromStorage(): boolean {
  if (typeof window === "undefined") {
    return true; // Default to expanded on SSR
  }
  try {
    const stored = localStorage.getItem("sidebar-open");
    if (stored === null) {
      return true; // Default to expanded if not set
    }
    return stored === "true";
  } catch {
    return true; // Default to expanded on error
  }
}

/**
 * Twin of the mounted rail's nav row: same 32px footprint, icon box, and
 * resting icon color. With an href it is a live Link with the rail's hover
 * and focus affordances, so a click landing during the skeleton window
 * navigates instead of dying on placeholder chrome; without one it stays a
 * plain div that shouldn't advertise interactivity.
 */
function RailSkeletonRow(t0) {
  const $ = _c(15);
  const {
    icon,
    active,
    slot,
    href
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="rail-row-icon flex size-8 shrink-0 items-center justify-center">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const contents = t1;
  if (href) {
    const t2 = active && RAIL_ROW_ACTIVE;
    let t3;
    if ($[2] !== t2) {
      t3 = cn(RAIL_ROW, t2);
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== contents || $[5] !== href || $[6] !== slot || $[7] !== t3) {
      t4 = <Link href={href} data-slot={slot} className={t3}>{contents}</Link>;
      $[4] = contents;
      $[5] = href;
      $[6] = slot;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  const t2 = active && RAIL_ROW_ACTIVE;
  let t3;
  if ($[9] !== t2) {
    t3 = cn("rail-row flex h-8 w-full items-center overflow-hidden rounded-md", "[&_svg]:text-neutral-600 dark:[&_svg]:text-neutral-400", t2);
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== contents || $[12] !== slot || $[13] !== t3) {
    t4 = <div data-slot={slot} className={t3}>{contents}</div>;
    $[11] = contents;
    $[12] = slot;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}

/**
 * Placeholder matching AppSwitcherRail's slim footprint: logo, org-switcher
 * pill, app rows, and the global footer (three icon rows + status widget), so
 * the rail never sits as a blank strip while user/org data loads. Every fixed
 * glyph is the mounted rail's own icon rather than a gray block: none of them
 * depend on the data being awaited, so a placeholder there would only flash
 * before the identical icon lands. Only the org pill, whose content is the
 * data being loaded, stays a `Skeleton`; the status widget is a static twin at
 * its own pending values (see RailStatusWidgetSkeleton), which keeps the footer
 * rows above it at their final height. The Codesmith app row follows the same
 * gate the mounted rail uses, so nothing pops in or out on swap.
 */
function RailSkeleton() {
  const $ = _c(40);
  const router = useRouter();
  const urlOrg = typeof router.query.org === "string" ? router.query.org : undefined;
  const isCodesmithRoute = isCodesmithPath(router.pathname);
  let t0;
  if ($[0] !== urlOrg) {
    t0 = getCachedCodesmithEnabled(urlOrg);
    $[0] = urlOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const showCodesmithRow = t0 !== false;
  const t1 = urlOrg ?? null;
  let t2;
  if ($[2] !== t1) {
    t2 = readCachedSidebarVariant(t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const redesign = t2 === "redesign";
  let t3;
  if ($[4] !== urlOrg) {
    t3 = urlOrg || getCachedLastActiveOrg();
    $[4] = urlOrg;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const navOrg = t3;
  const activeSection = router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isStandaloneAccountPath(router.pathname) ? null : isCodesmithRoute ? "codesmith" : "actions";
  const t4 = redesign && RAIL_REDESIGN_CLASS;
  let t5;
  if ($[6] !== t4) {
    t5 = cn("h-full shrink-0 bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]", t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH;
  let t7;
  if ($[8] !== t6) {
    t7 = {
      width: t6
    };
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <AppHeaderBar><div className="flex size-8 shrink-0 items-center justify-center"><div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div></div></AppHeaderBar>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = redesign ? undefined : "pt-app-header-gap";
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Skeleton className="h-8 w-full rounded-lg" />;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const t12 = !redesign && "gap-1 pt-1";
  let t13;
  if ($[14] !== t12) {
    t13 = cn("rail-rows flex flex-col", t12);
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Github className="size-[18px]" />;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const t15 = activeSection === "actions";
  const t16 = navOrg ? `/${navOrg}/runs/jobs` : undefined;
  let t17;
  if ($[17] !== t15 || $[18] !== t16) {
    t17 = <RailSkeletonRow icon={t14} active={t15} href={t16} />;
    $[17] = t15;
    $[18] = t16;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] !== activeSection || $[21] !== navOrg || $[22] !== showCodesmithRow) {
    t18 = showCodesmithRow && <RailSkeletonRow icon={<CodesmithIcon />} active={activeSection === "codesmith"} slot="rail-skeleton-codesmith-row" href={navOrg ? `/${navOrg}/sessions/new` : undefined} />;
    $[20] = activeSection;
    $[21] = navOrg;
    $[22] = showCodesmithRow;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== t13 || $[25] !== t17 || $[26] !== t18) {
    t19 = <div className={t13}>{t17}{t18}</div>;
    $[24] = t13;
    $[25] = t17;
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="flex-1" />;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== activeSection || $[30] !== redesign) {
    t21 = redesign ? <div className="rail-rows flex flex-col pb-2"><RailSkeletonRow icon={<BarChart3 className="size-[18px]" />} active={activeSection === "usage"} /><RailSkeletonRow icon={<Bell className="size-[18px]" />} /><RailSkeletonRow icon={<SettingsIcon className="size-[18px]" />} /><RailStatusWidgetSkeleton /></div> : <div className="rail-rows flex flex-col gap-1 pb-2"><RailSkeletonRow icon={<BarChart3 className="size-[18px]" />} active={activeSection === "usage"} /><RailSkeletonRow icon={<SettingsIcon className="size-[18px]" />} /><RailSkeletonRow icon={<Bell className="size-[18px]" />} /><div className="pt-1"><RailStatusWidgetSkeleton /></div></div>;
    $[29] = activeSection;
    $[30] = redesign;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== t11 || $[33] !== t19 || $[34] !== t21) {
    t22 = <div className="flex h-full w-full flex-col px-2">{t8}{t11}{t19}{t20}{t21}</div>;
    $[32] = t11;
    $[33] = t19;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t22 || $[37] !== t5 || $[38] !== t7) {
    t23 = <div className={t5} style={t7} data-slot="app-switcher-rail-skeleton">{t22}</div>;
    $[36] = t22;
    $[37] = t5;
    $[38] = t7;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  return t23;
}

/**
 * Full-page loading skeleton: rail + sidebar skeleton + centered logo in main
 * area, or rail + SessionSkeleton on codesmith routes (which have no Actions
 * sidebar; the session sidebar lives inside the main pane at a persisted width).
 * Used during initial auth check (AuthProvider) so the loading state matches app chrome.
 * AuthProvider only renders this on the client after hydration, so
 * localStorage can be read synchronously on first paint.
 */
export function SidebarLayoutSkeleton() {
  const $ = _c(17);
  const router = useRouter();
  const [sidebarOpen] = useState(getSidebarStateFromStorage);
  let t0;
  if ($[0] !== router.pathname) {
    t0 = isCodesmithPath(router.pathname);
    $[0] = router.pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isCodesmithRoute = t0;
  let t1;
  if ($[2] !== router.pathname) {
    t1 = isStandaloneAccountPath(router.pathname);
    $[2] = router.pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isStandalone = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RailSkeleton />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isCodesmithRoute || $[6] !== isStandalone || $[7] !== sidebarOpen) {
    t3 = !isCodesmithRoute && <SidebarSkeleton collapsed={!sidebarOpen} standalone={isStandalone} />;
    $[5] = isCodesmithRoute;
    $[6] = isStandalone;
    $[7] = sidebarOpen;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== isCodesmithRoute) {
    t4 = <SidebarInset className="flex-1 bg-transparent! overflow-y-auto [scrollbar-gutter:stable]">{isCodesmithRoute ? <SessionSkeleton /> : <div className="flex h-full items-center justify-center"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>}</SidebarInset>;
    $[9] = isCodesmithRoute;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex-1 flex overflow-hidden">{t2}{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== sidebarOpen || $[15] !== t5) {
    t6 = <div className="flex flex-col h-dvh"><SidebarProvider open={sidebarOpen} className="flex-1 min-h-0">{t5}</SidebarProvider></div>;
    $[14] = sidebarOpen;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
export function SidebarLayout(t0) {
  const $ = _c(48);
  const {
    children,
    scrollableMain: t1
  } = t0;
  const scrollableMain = t1 === undefined ? true : t1;
  const [sidebarOpen, setSidebarOpen] = useLocalStorage("sidebar-open", true);
  const [sidebarPreference, setSidebarPreference] = useLocalStorage("sidebar-preference", "auto");
  const {
    shortcutsModalOpen,
    setShortcutsModalOpen
  } = useShortcuts();
  const router = useRouter();
  let t2;
  if ($[0] !== router.pathname) {
    t2 = isCodesmithPath(router.pathname);
    $[0] = router.pathname;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isCodesmithRoute = t2;
  let t3;
  if ($[2] !== isCodesmithRoute || $[3] !== router.pathname || $[4] !== scrollableMain) {
    t3 = scrollableMain === false || isCodesmithRoute || /^(\/\[org\])?\/(usage|settings)(\/|$)/.test(router.pathname) || router.pathname === "/admin" || router.pathname === "/[org]/cache/[repository]";
    $[2] = isCodesmithRoute;
    $[3] = router.pathname;
    $[4] = scrollableMain;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const fixedMain = t3;
  let t4;
  if ($[6] !== router.asPath || $[7] !== router.query.embed) {
    t4 = router.query.embed === "1" || /(?:^|[?&])embed=1(?:&|$)/.test(router.asPath.split("#")[0] ?? "");
    $[6] = router.asPath;
    $[7] = router.query.embed;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isEmbed = t4;
  const activeApp = useActiveApp();
  let t5;
  if ($[9] !== router.pathname) {
    t5 = isStandaloneAccountPath(router.pathname);
    $[9] = router.pathname;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const isStandalone = t5;
  const showContextualSidebar = !isEmbed && activeApp === "actions" && !isStandalone;
  let t6;
  if ($[11] !== router.pathname) {
    t6 = isSettingsPath(router.pathname);
    $[11] = router.pathname;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const isSettingsRoute = t6;
  const showSettingsSidebar = !isEmbed && isSettingsRoute;
  useWorkflowCache();
  usePrefetchAssistantSessions();
  usePrefetchCodesmithChunk();
  let t7;
  if ($[13] !== setSidebarOpen || $[14] !== setSidebarPreference) {
    t7 = open => {
      setSidebarOpen(open);
      setSidebarPreference("manual");
    };
    $[13] = setSidebarOpen;
    $[14] = setSidebarPreference;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleSidebarChange = t7;
  let t8;
  let t9;
  if ($[16] !== setSidebarOpen || $[17] !== sidebarPreference) {
    t8 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const mediaQueryMD = window.matchMedia("(min-width: 768px)");
      const mediaQueryXL = window.matchMedia("(min-width: 1280px)");
      const updateSidebarState = () => {
        const isMD = mediaQueryMD.matches;
        const isXL = mediaQueryXL.matches;
        const shouldOpen = isMD && isXL;
        setSidebarOpen(currentOpen => {
          if (currentOpen !== shouldOpen) {
            return shouldOpen;
          }
          return currentOpen;
        });
      };
      if (sidebarPreference === "auto") {
        updateSidebarState();
      }
      const handleChange = () => {
        if (sidebarPreference === "manual") {
          return;
        }
        updateSidebarState();
      };
      if (mediaQueryMD.addEventListener && mediaQueryXL.addEventListener) {
        mediaQueryMD.addEventListener("change", handleChange);
        mediaQueryXL.addEventListener("change", handleChange);
        return () => {
          mediaQueryMD.removeEventListener("change", handleChange);
          mediaQueryXL.removeEventListener("change", handleChange);
        };
      } else {
        mediaQueryMD.addListener(handleChange);
        mediaQueryXL.addListener(handleChange);
        return () => {
          mediaQueryMD.removeListener(handleChange);
          mediaQueryXL.removeListener(handleChange);
        };
      }
    };
    t9 = [sidebarPreference, setSidebarOpen];
    $[16] = setSidebarOpen;
    $[17] = sidebarPreference;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  const t10 = showContextualSidebar || showSettingsSidebar;
  let t11;
  if ($[20] !== isEmbed) {
    t11 = !isEmbed && <Suspense fallback={<RailSkeleton />}><AppSwitcherRail /></Suspense>;
    $[20] = isEmbed;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== showContextualSidebar) {
    t12 = showContextualSidebar && <Suspense fallback={<SidebarSkeleton collapsed={!getSidebarStateFromStorage()} />}><Sidebar /></Suspense>;
    $[22] = showContextualSidebar;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== showSettingsSidebar) {
    t13 = showSettingsSidebar && <Suspense fallback={<SidebarSkeleton collapsed={!getSidebarStateFromStorage()} standalone={true} />}><SettingsSidebar /></Suspense>;
    $[24] = showSettingsSidebar;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = isCodesmithRoute ? "codesmith" : isSettingsRoute ? "settings" : router.pathname;
  let t15;
  if ($[26] !== children || $[27] !== fixedMain || $[28] !== t14) {
    t15 = <SidebarMainContent scrollResetKey={t14} fixedMain={fixedMain}>{children}</SidebarMainContent>;
    $[26] = children;
    $[27] = fixedMain;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== t13 || $[33] !== t15) {
    t16 = <div className="flex-1 flex overflow-hidden">{t11}{t12}{t13}{t15}</div>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== handleSidebarChange || $[36] !== sidebarOpen || $[37] !== t10 || $[38] !== t16) {
    t17 = <SidebarProvider open={sidebarOpen} onOpenChange={handleSidebarChange} enableKeyboardShortcut={t10} className="flex-1 min-h-0 relative">{t16}</SidebarProvider>;
    $[35] = handleSidebarChange;
    $[36] = sidebarOpen;
    $[37] = t10;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== setShortcutsModalOpen || $[41] !== shortcutsModalOpen) {
    t18 = <ShortcutsModal open={shortcutsModalOpen} onOpenChange={setShortcutsModalOpen} />;
    $[40] = setShortcutsModalOpen;
    $[41] = shortcutsModalOpen;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <CommandPalette />;
    t20 = <LiveMetricsSheetLoader />;
    $[43] = t19;
    $[44] = t20;
  } else {
    t19 = $[43];
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t17 || $[46] !== t18) {
    t21 = <div className="flex flex-col h-dvh">{t17}{t18}{t19}{t20}</div>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  return t21;
}

// Every page shares these helpers so the OrgUrlSyncProvider > SidebarLayout
// element shape is identical across routes and React reconciles the persistent
// chrome instead of remounting it. useOrgUrlSync is a no-op on routes without
// a URL org and skips /admin/internal-monitors, so mounting it globally is safe.
export function getSidebarLayout(page: ReactElement) {
  return <OrgUrlSyncProvider>
      <SidebarLayout>{page}</SidebarLayout>
    </OrgUrlSyncProvider>;
}

// For pages whose content owns its scroll (scrollableMain={false}).
export function getFixedSidebarLayout(page: ReactElement) {
  return <OrgUrlSyncProvider>
      <SidebarLayout scrollableMain={false}>{page}</SidebarLayout>
    </OrgUrlSyncProvider>;
}